import React, { useContext, useState } from 'react';
import { useHistory, Link } from 'react-router-dom';
import Subject from '@mui/icons-material/Subject';
import NiceModal from '@ebay/nice-modal-react';
import makeStyles from '@mui/styles/makeStyles';
import { lighten } from '@mui/material/styles';
import { AppBar, Box, Button, Toolbar, Stack } from '@mui/material';
import useIsMobile from 'hooks/useIsMobile';
import './overrides.css';
import SiteMenu from './SiteMenu';
import isKeyDown from 'common/utils/keys';
import GlobalSearch from './GlobalSearch';
import { UserContext } from 'common/context/UserContext';
import ClientIcon from 'common/components/Icons/v2/Client';
import { ReactComponent as ItrvlLogo } from 'common/assets/itrvl_logo--all-white.svg';
import CreateAddClientModal from 'components/v2/Modals/CreateAddClientModal';
import EdgeEnquiryModal from 'pages/Edge/components/EdgeEnquiryModal';
import ClientMenuButton from 'components/v2/ClientMenuButton';

import logger from 'itrvl-logger';
const log = logger(__filename);
log.trace(__filename);

const useStyles = makeStyles(theme => ({
  root: {
    zIndex: 10,
    width: '100%',
    display: 'flex',
    alignItems: 'center',
  },
  appBar: {
    zIndex: theme.zIndex.drawer,
  },
  toolBar: {
    flex: 1,
  },
  logo: {
    flexShrink: 0,
    height: 'auto',
    display: 'block',
    lineHeight: '0',
    '& svg': {
      width: '100%',
      height: '100%',
    },
    [theme.breakpoints.down('sm')]: {
      width: 100,
      padding: 6,
    },
    [theme.breakpoints.up('sm')]: {
      width: 148,
      padding: 9,
    },
  },
  name: {
    marginLeft: '20px',
    color: 'white',
  },
  growHalf: {
    flexGrow: 0.5,
  },
  menuButton: {
    marginLeft: -12,
    marginRight: 20,
  },
  menuItem: {
    padding: '6px 13px',
    textTransform: 'none',
    fontWeight: theme.typography.fontWeightBold,
  },
  clientDetailsButton: {
    '&:hover': { color: 'white' },
    color: 'white',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
    backgroundColor: theme.palette.secondary.main,
    padding: '10px',
    height: '85px',
    width: '95px',
    textAlign: 'center',
  },
  menuBtn: {
    color: '#fff',
    height: 69,
    width: 69,
  },
  clientInfo: {
    fontFamily: 'Atyp Display',
    color: '#fff',
    '& button': {
      fontFamily: 'Atyp Display',
      textTransform: 'capitalize',
      color: 'white',
      backgroundColor: theme.palette.primary.light,
      borderRadius: '10px',
      '&:hover': {
        backgroundColor: lighten(theme.palette.primary.light, 0.1),
      },
    },
  },
  iconLabel: {
    fontSize: '9px',
    fontWeight: theme.typography.fontWeightMedium,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  },
  iconDrawer: {
    fontSize: '1.5rem',
  },
}));

const enquiryCtaSx = {
  px: 2,
  py: 1,
  textTransform: 'uppercase',
  fontWeight: 500,
  fontSize: '12px',
  borderRadius: 1,
  backgroundColor: 'darkGreen.white20',
  color: 'stoneGrey.white80',
  boxShadow: 'none',
  '&:hover': { backgroundColor: 'darkGreen.white40', boxShadow: 'none' },
};

const ItrvlAppBar = ({ useFixedAppbar }) => {
  const classes = useStyles();
  const isMobile = useIsMobile();
  const userContext = useContext(UserContext);
  const history = useHistory();
  const [showSiteMenu, setShowSiteMenu] = useState(false);

  const handleLogout = () => {
    userContext.logout();
  };

  const onSelectClient = () => {
    NiceModal.show(CreateAddClientModal, {
      canDelete: false,
      agencyId: userContext.agency.id,
      onSelectExistingClient: async (client, modal) => {
        await userContext.updateClient(client);
        modal.hide();
        history.push(`/clients/${client.id}`);
      },
      client: userContext.client,
      showCurrentClient: true,
    });
  };

  const wrappedLogo = (
    <Link to={() => (isKeyDown(83) ? '/sentryError' : isKeyDown(67) ? '/config' : '/')}>
      <ItrvlLogo alt="itrvl" />
    </Link>
  );
  const menu = (
    <>
      <Button
        className={classes.menuBtn}
        onClick={() => setShowSiteMenu(true)}
        aria-label="Navigation Menu"
        data-test-id="navigation-menu-button"
      >
        <Subject />
      </Button>
      <SiteMenu open={showSiteMenu} logout={handleLogout} onClose={() => setShowSiteMenu(false)} />
    </>
  );

  return (
    <div className={classes.root}>
      <AppBar className={classes.appBar} position={useFixedAppbar ? 'fixed' : 'static'} color="primary" data-test-id="app-bar-container">
        <Toolbar className={classes.toolBar} disableGutters>
          {menu}
          <div className={classes.logo}>{wrappedLogo}</div>
          <Stack direction="row" spacing={2} useFlexGap alignItems="center" justifyContent="flex-end" sx={{ flex: 1, mr: 2 }}>
            {userContext.isRetail() && (
              <Button
                onClick={() => NiceModal.show(EdgeEnquiryModal)}
                variant="contained"
                data-test-id="navbar-enquiry-cta"
                sx={enquiryCtaSx}
              >
                Request A Custom Quote
              </Button>
            )}
            {!userContext.isRetail() && (
              <>
                <GlobalSearch />
                {isMobile ? (
                  <ClientMenuButton />
                ) : (
                  <>
                    <Box className={classes.clientInfo} data-test-id="client-info">
                      <Button onClick={onSelectClient} color="secondary" data-test-id="client-info-selector" sx={{ px: 3.5 }}>
                        {userContext.client ? `Client: ${userContext.client.name}` : 'Select Client'}
                      </Button>
                    </Box>
                    {userContext.client?.id && (
                      <Link
                        to={`/clients/${userContext.client.id}`}
                        data-test-id="navigation-client-details"
                        className={classes.clientDetailsButton}
                      >
                        <span className={classes.iconDrawer}>
                          <ClientIcon />
                        </span>
                        <div className={classes.iconLabel}>Client Details</div>
                      </Link>
                    )}
                  </>
                )}
              </>
            )}
          </Stack>
        </Toolbar>
      </AppBar>
    </div>
  );
};

export default ItrvlAppBar;
